import React from "react";
import { formatDateFromTimestamp as formatDateLocal, formatDateTimeUtcFromTimestamp } from "../utils/dateUtils";
import { useState, useEffect, useRef } from "react";
import FifoDetailRow from "./FifoDetailRow";
import { useMemo } from "react";
import { LayoutDashboard } from "lucide-react";
import { useLanguage } from '../contexts/LanguageContext';
import { t, getTaxCategoryLabel } from '../utils/translations';
import { resolveV3TaxPlanName } from "../utils/v3taxPlanLabel.js";
import { normalizeSearchText } from "../utils/classificationLabel.js";
import V3TaxPurchaseTypeLabel from "./V3TaxPurchaseTypeLabel.jsx";
import MaskedEurCell from "./MaskedEurCell";
import {
  getPreviewTaxIssueKind,
  PreviewTaxIssueBadge,
  PreviewTaxIssueCell,
} from "./PreviewTaxIssue";
import { cn, tableTheme as T, analysisTone } from "./ui/analysisTableTheme.js";
import UnifiedTableEmptyState, { computeTableEmptyMetrics } from "./UnifiedTableEmptyState.jsx";
import {
  pickSectionTotals,
  currencySymbolOf,
  pickFiatPrice,
  pickFiatTotal,
  pickFiatTaxable,
} from "../utils/fiatFields";
import {
  shouldMaskPreviewEurRow,
  shouldShowPreviewSeparatorAfterRow,
} from "../utils/previewMode";
import PreviewTableNotice from "./PreviewTableNotice";
import PreviewModeSeparator from "./PreviewModeSeparator";
import TaxTreatmentValue from "./TaxTreatmentValue.jsx";
import { rowMatchesTaxYear, resolveDisplayTaxYearLabel } from '../utils/taxYear.js';
import { buildTaxTableSectionData } from '../utils/taxTableSectionData.js';
import {
  isSquirtleSquadClass,
  renderSquirtleSquadType,
} from "../config/squirtleSquadClassifications.jsx";
import { JUICY_TAX_TYPE_FILTER_OPTIONS } from "../config/juicyFinanceClassifications.jsx";
import GasAbstractionFeeLabel from "./GasAbstractionFeeLabel.jsx";
import { getGasAbstractionFeePlainLabel } from "../utils/gasAbstractionFeeLabel.js";
import DexAggregatorExecutionCostLabel, {
  getDexAggregatorExecutionCostPlainLabel,
  isDexAggregatorExecutionCostClass,
} from "./DexAggregatorExecutionCostLabel.jsx";
import VaultXTypeLabel from "./VaultXTypeLabel.jsx";
import { getVaultXDisplay } from "../utils/vaultxDisplay.js";
import {
  VET_DOMAINS_ICON_SRC,
  getVetDomainsDisplay,
} from "../utils/vetDomainsDisplay.js";

const HeaderIconBadge = ({ children, className = "" }) => (
  <div className={cn(T.headerMediaBadge, className)}>
    <div className="absolute inset-0 bg-gradient-to-br from-white via-white/85 to-slate-50/80" />
    <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
      <div className="absolute -top-2 -left-3 h-5 w-12 rotate-12 bg-white/50 blur-md" />
    </div>
    <div className="relative z-10 flex items-center justify-center">
      {children}
    </div>
  </div>
);

// 🧪 Preview: Helper für sichere Zahl-Formatierung
const fmtFixed = (v, d = 2) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(d) : "—";
};

// 🚀 Helper-Funktion für Timestamp-Normalisierung
const toSec = (ts) => (Number(ts) > 1e12 ? Math.floor(Number(ts) / 1000) : Number(ts));

function isJuicyFinanceClass(cls) {
  return String(cls || "").trim().toLowerCase().startsWith("juicyfinance_");
}

function isWovAuctionBidClass(cls) {
  const c = String(cls || "").trim().toLowerCase();
  return (
    c === "nft_marketplace_vet_bid_deposit" ||
    c === "nft_marketplace_vet_bid_refund" ||
    c === "nft_marketplace_token_bid_deposit" ||
    c === "nft_marketplace_token_bid_refund"
  );
}

function isWovCreatorRoyaltyClass(cls) {
  return String(cls || "").trim().toLowerCase() === "nft_creator_royalty";
}

function renderWorldOfVCreatorRoyaltyType(language) {
  const subtitle =
    language === "de" ? "Creator Royalty" : "Creator Royalty";

  return (
    <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
      <img
        src="/wov.png"
        alt="World of V"
        className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
        draggable={false}
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
      <div className="min-w-0 leading-tight">
        <div className="whitespace-normal break-words">World of V</div>
        <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
          {subtitle}
        </div>
      </div>
    </div>
  );
}

function renderWorldOfVAuctionBidType(clsRaw, language) {
  const cls = String(clsRaw || "").trim().toLowerCase();
  const isRefund = cls.endsWith("_refund");

  const subtitle = isRefund
    ? language === "de"
      ? "Auktionsgebot zurückerstattet"
      : "Auction Bid Refund"
    : language === "de"
      ? "Auktionsgebot hinterlegt"
      : "Auction Bid Deposit";

  return (
    <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
      <img
        src="/wov.png"
        alt="World of V"
        className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
        draggable={false}
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
      <div className="min-w-0 leading-tight">
        <div className="whitespace-normal break-words">World of V</div>
        <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
          {subtitle}
        </div>
      </div>
    </div>
  );
}

function toTitleCase(input) {
  return String(input || "")
    .replace(/_/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function getJuicyFinanceDisplay(clsRaw, tokenRaw = "") {
  const cls = String(clsRaw || "").trim().toLowerCase();
  const token = String(tokenRaw || "").trim().toUpperCase();

  const assetOpMatch = cls.match(/^juicyfinance_([a-z0-9]+)_(deposit|withdrawal|borrow|repay)$/i);
  if (assetOpMatch) {
    const [, asset, action] = assetOpMatch;
    return {
      title: "JuicyFinance",
      subtitle: `${String(asset || "").toUpperCase()} ${toTitleCase(action)}`,
    };
  }

  switch (cls) {
    case "juicyfinance_lending_income":
      return { title: "JuicyFinance", subtitle: token ? `${token} Lending Income` : "Lending Income" };
    case "juicyfinance_borrow_principal":
      return { title: "JuicyFinance", subtitle: token ? `${token} Borrow Principal` : "Borrow Principal" };
    case "juicyfinance_repay_principal":
      return { title: "JuicyFinance", subtitle: token ? `${token} Repay Principal` : "Repay Principal" };
    case "juicyfinance_borrow_interest_paid":
      return { title: "JuicyFinance", subtitle: token ? `${token} Borrow Interest Paid` : "Borrow Interest Paid" };
    case "juicyfinance_unmatched_withdrawal_principal":
      return {
        title: "JuicyFinance",
        subtitle: token
          ? `${token} Withdrawal (Principal unbekannt)`
          : "Withdrawal (Principal unbekannt)",
      };
    default:
      return {
        title: "JuicyFinance",
        subtitle: toTitleCase(cls.replace(/^juicyfinance_/i, "")),
      };
  }
}

function renderJuicyFinanceType(clsRaw, tokenRaw = "") {
  const { title, subtitle } = getJuicyFinanceDisplay(clsRaw, tokenRaw);

  return (
    <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
      <img
        src="/JF.png"
        alt="JuicyFinance"
        className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
        draggable={false}
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
      <div className="min-w-0 leading-tight">
        <div className="whitespace-normal break-words">{title}</div>
        <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
          {subtitle}
        </div>
      </div>
    </div>
  );
}

function isSolarwiseClass(cls) {
  return String(cls || "").trim().toLowerCase().startsWith("solarwise_");
}

function renderVetDomainsClassification(classification, tx = null) {
  const display = getVetDomainsDisplay(classification, tx);
  if (!display) return null;

  return (
    <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
      <img
        src={VET_DOMAINS_ICON_SRC}
        alt="vet.domains"
        className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
        draggable={false}
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
      <div className="min-w-0 leading-tight">
        <div className="whitespace-normal break-words">{display.title}</div>
        <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
          {display.subtitle}
        </div>
      </div>
    </div>
  );
}

/** Gleiche Optik wie RawTransfersTable: blau + solarwise.png + zweizeilig */
function renderSolarwiseClassification(clsRaw) {
  const cls = String(clsRaw || "").trim().toLowerCase();

  if (cls === "solarwise_electricity_revenue") {
    return (
      <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
        <img
          src="/solarwise.png"
          alt=""
          className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
          draggable={false}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        <div className="min-w-0 leading-tight">
          <div className="whitespace-normal break-words">Solarwise</div>
          <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
            Electricity Revenue
          </div>
        </div>
      </div>
    );
  }
  if (cls === "solarwise_stake_deposit") {
    return (
      <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
        <img
          src="/solarwise.png"
          alt=""
          className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
          draggable={false}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        <div className="min-w-0 leading-tight">
          <div className="whitespace-normal break-words">Solarwise</div>
          <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
            Stake Deposit
          </div>
        </div>
      </div>
    );
  }
  if (cls === "solarwise_stake_withdrawal") {
    return (
      <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
        <img
          src="/solarwise.png"
          alt=""
          className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
          draggable={false}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        <div className="min-w-0 leading-tight">
          <div className="whitespace-normal break-words">Solarwise</div>
          <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
            Stake Withdrawal
          </div>
        </div>
      </div>
    );
  }

  let label = clsRaw || "";
  if (cls === "solarwise_nft_stake_deposit") label = "Solarwise NFT Stake Deposit";
  else if (cls === "solarwise_nft_stake_withdrawal") label = "Solarwise NFT Stake Withdrawal";
  else if (cls === "solarwise_certificate_mint") label = "Solarwise Certificate Mint";
  else if (cls === "solarwise_certificate_burn") label = "Solarwise Certificate Burn";
  else
    label = String(clsRaw || "")
      .replace(/^solarwise_/i, "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());

  return (
    <span className="text-blue-600 font-semibold">
      ☀️ {label}
    </span>
  );
}

// swap_out und nfbc_purchase: Disposals mit FIFO-Details im PDF wie Swap
const isSquadPreicoPaymentLike = (cls) => {
  return String(cls || "").toLowerCase() === "squad_preico_payment";
};

const isSwapOutLike = (cls) => {
  const c = String(cls || "").toLowerCase();
  return c === "swap_out" || c === "nfbc_purchase" || c === "v3tax_purchase" || c === "pause_your_carbon_purchase" || isSquadPreicoPaymentLike(c) || isDexAggregatorExecutionCostClass(c);
};

// Helper: Soll diese Transaktion im PDF alle Details zeigen?
const isImportantFifoTx = (tx) => {
  const type = (tx.type || tx.event || "").toLowerCase();
  const classification = (tx.classification || "").toLowerCase();

  return (
    isSwapOutLike(type) ||
    isSwapOutLike(classification) ||
    type === "gas_abstraction_fee" ||
    classification === "gas_abstraction_fee" ||
    type === "nft_mint" ||
    classification === "nft_mint" ||
    type === "nft_galaxy_mint" ||
    classification === "nft_galaxy_mint" ||
    type === "nft_marketplace_purchase_payment" ||
    classification === "nft_marketplace_purchase_payment" ||
    type === "sale_payment" ||
    classification === "sale_payment"
  );
};

export default function VetTaxTable({
  datasetId,
  year,
  displayTaxYear,
  country = 'DE',
  vetDataFromProps = [],
  vetTotalsFromProps = {},
  onTotalProfitCalculated,
  onDataReady,
  isEditMode = false,
  selectedCountry = 'DE',
  pdfExportMode = false,
  isPreviewMode = false,
  previewMaskedTotalEUR = null,
  previewMaskedTotals = null,
  enableExportCallback = true,
}) {
  // 🌐 Sprachverwaltung
  const { language } = useLanguage();
  const tone = analysisTone.indigo;
  const primaryTableWrapClass = "v3-primary-table-shell v3-primary-table-shell--indigo";
  const primaryTableHeadClass = "v3-primary-table-head v3-primary-table-head--indigo";

  const totalsMeta = useMemo(
    () => pickSectionTotals(vetTotalsFromProps || {}, selectedCountry),
    [vetTotalsFromProps, selectedCountry]
  );
  const currencyCode = totalsMeta.currency;
  const currencySymbol = currencySymbolOf(currencyCode);
  // 🎯 NEU: Backend-Daten State
  const [vetData, setVetData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // 🎯 NEU: UI State (nur für Anzeige)
  const [sortKey, setSortKey] = useState("timestamp");
  const [sortDirection, setSortDirection] = useState("desc");
  const [search, setSearch] = useState("");
  const [tokenFilter, setTokenFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [expandedIndex, setExpandedIndex] = useState(null);

  const toggleRow = (index) => {
    // Im PDF-Modus keine Click-Toggles, damit sich nichts verändert,
    // während der Browser druckt
    if (pdfExportMode) return;
    setExpandedIndex((prev) => (prev === index ? null : index));
  };
  
  // 🚀 WICHTIG: Alle Hooks müssen vor frühen Returns kommen
  const effectiveYear = String(year || ""); // Kein Fallback auf aktuelles Jahr
  const yearLabel = resolveDisplayTaxYearLabel(year, selectedCountry, displayTaxYear);

  const pdfSectionData = useMemo(() => {
    if (!pdfExportMode) return null;
    return buildTaxTableSectionData(
      vetDataFromProps,
      vetTotalsFromProps,
      effectiveYear,
      selectedCountry
    );
  }, [pdfExportMode, vetDataFromProps, vetTotalsFromProps, effectiveYear, selectedCountry]);

  const activeRows = pdfSectionData?.rows ?? vetData.rows;
  const activeTotals = pdfSectionData?.totals ?? vetData.totals;
  
  // 🚨 WICHTIG: Ref um mehrfache Ausführung zu verhindern
  const hasProcessedDataRef = useRef(false);
  const prevDataLengthRef = useRef(0);
  const prevTotalsRef = useRef({});

  // 🎯 NEU: Props-Effekt (wie VthoTaxTable)
  useEffect(() => {
    if (!Array.isArray(vetDataFromProps)) return;
    
    // Filter Rows auf das gewählte Jahr (für Tabellen-Anzeige)
    const yearRows = vetDataFromProps.filter(tx => {
      if (!tx.timestamp) return false;
      if (!effectiveYear) return true; // Wenn kein Jahr gesetzt, zeige alle
      return rowMatchesTaxYear(tx, selectedCountry, Number(effectiveYear));
    });

    const m = pickSectionTotals(vetTotalsFromProps || {}, selectedCountry);
    const totals = {
      swapProfit: m.disposalNet,
      swapGain: m.disposalGain,
      swapLoss: m.disposalLoss,
      otherIncome: m.otherIncome,
      totalTaxable: m.totalTaxable,
      disposalShortTermNetFiat: m.disposalShortTermNet,
      disposalLongTermNetFiat: m.disposalLongTermNet,
    };
    
    // 🛡️ SCHUTZ: Nur setState bei echter Datenänderung
    const dataChanged = yearRows.length !== prevDataLengthRef.current ||
                        totals.totalTaxable !== prevTotalsRef.current.totalTaxable;
    
    if (dataChanged) {
      setVetData({ rows: yearRows, totals });
      prevDataLengthRef.current = yearRows.length;
      prevTotalsRef.current = totals;
      hasProcessedDataRef.current = true;
    }
    
  }, [vetDataFromProps, vetTotalsFromProps, effectiveYear, selectedCountry]);

  // 🎯 NEU: Export-Callback (wie VTHO)
  useEffect(() => {
    if (!onDataReady || isPreviewMode || !enableExportCallback) return;
    const csvLocale =
      String(selectedCountry || "DE").toUpperCase().includes("US")
        ? "en-US"
        : "de-DE";

    const exportable = (vetData.rows || []).map((r) => ({
      type: r.classification,
      token: "VET",
      amount: r.amount,
      fiatPrice: pickFiatPrice(r),
      totalFiat: pickFiatTotal(r),
      taxableProfitFiat: pickFiatTaxable(r) ?? 0,
      euroPrice: pickFiatPrice(r) ?? r.euroPrice ?? null,
      totalEUR: pickFiatTotal(r) ?? r.euroAmount ?? null,
      fifoProfitEUR: pickFiatTaxable(r) ?? r.taxableProfitEUR ?? r.taxableProfit ?? 0,
      hash: r.hash || "",
      date: r.timestamp
        ? new Date(toSec(r.timestamp) * 1000).toLocaleString(csvLocale, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          }).replace(",", "")
        : "",
    }));
    onDataReady(exportable);
  }, [vetData.rows, selectedCountry, isPreviewMode, onDataReady, enableExportCallback]);

  // 🎯 NEU: Summen an Parent melden (wie VTHO)
  useEffect(() => {
    if (isPreviewMode) return;
    if (onTotalProfitCalculated && vetData.totals) {
      onTotalProfitCalculated({
        swapProfit: vetData.totals.swapProfit,
        otherIncome: 0,
        total: vetData.totals.totalTaxable
      });
    }
  }, [vetData.totals]); // ✅ onTotalProfitCalculated entfernt um Endlosschleife zu vermeiden

  // 🎯 NEU: Anzeige-Liste (nur UI, keine Logik)
  const filtered = useMemo(() => {
    if (!activeRows || activeRows.length === 0) return [];
    
    let filtered = activeRows.filter(tx => {
      // Token-Filter
      if (tokenFilter !== "ALL" && tx.token !== tokenFilter) return false;
      
      // Type-Filter (nur swap_out für VET)
      if (typeFilter !== "ALL" && tx.classification !== typeFilter) return false;
      
      // Search-Filter
      if (search) {
        const searchLower = search.toLowerCase();
        const searchNormalized = normalizeSearchText(search);
        return (
          (tx.hash && tx.hash.toLowerCase().includes(searchLower)) ||
          (tx.token && tx.token.toLowerCase().includes(searchLower)) ||
          (tx.classification && tx.classification.toLowerCase().includes(searchLower)) ||
          (searchNormalized !== "" &&
            normalizeSearchText(tx.classification || tx.type || "").includes(
              searchNormalized
            ))
        );
      }
      
      return true;
    });
    
    // Sortierung
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortKey) {
        case "timestamp":
          aVal = toSec(a.timestamp);
          bVal = toSec(b.timestamp);
          break;
        case "amount":
          aVal = Number(a.amount || 0);
          bVal = Number(b.amount || 0);
          break;
        case "euroPrice":
          aVal = Number(a.euroPrice || 0);
          bVal = Number(b.euroPrice || 0);
          break;
        case "euroAmount":
          aVal = Number(a.euroAmount || 0);
          bVal = Number(b.euroAmount || 0);
          break;
        case "taxableProfit":
          aVal = Number(a.taxableProfitEUR ?? a.taxableProfit ?? 0);
          bVal = Number(b.taxableProfitEUR ?? b.taxableProfit ?? 0);
          break;
        case "hash":
          aVal = (a.hash || a.txid || a.event_id || '').toLowerCase();
          bVal = (b.hash || b.txid || b.event_id || '').toLowerCase();
          break;
        case "classification":
          aVal = (a.classification || '').toLowerCase();
          bVal = (b.classification || '').toLowerCase();
          break;
        default:
          aVal = a[sortKey] || "";
          bVal = b[sortKey] || "";
      }
      
      if (sortDirection === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    return filtered;
  }, [activeRows, tokenFilter, typeFilter, search, sortKey, sortDirection]);

  useEffect(() => {
    setExpandedIndex(null);
  }, [sortKey, sortDirection, search, tokenFilter, typeFilter, filtered.length]);

  const emptyMetrics = useMemo(
    () =>
      computeTableEmptyMetrics({
        allRows: vetDataFromProps,
        yearRows: activeRows,
        filteredRows: filtered,
        effectiveYear,
      }),
    [vetDataFromProps, activeRows, filtered, effectiveYear]
  );

  // 🎯 NEU: Summen-Berechnung für Anzeige
  const totals = useMemo(() => {
    if (!activeTotals) return { swapProfit: 0, otherIncome: 0, totalTaxable: 0 };
    return activeTotals;
  }, [activeTotals]);

  // 🎯 NEU: Formatierungsfunktionen
  const formatDateFromTimestamp = (timestamp) => {
    if (!timestamp) return "";
    if (pdfExportMode) {
      return formatDateTimeUtcFromTimestamp(timestamp);
    }
    return formatDateLocal(timestamp);
  };

  // 🔧 ENTFERNT: Doppelter useEffect der die korrekte Datenstruktur überschreibt

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const getArrow = (key) => {
    if (key !== sortKey) return "";
    return sortDirection === "asc" ? " ↑" : " ↓";
  };






  // 🎯 NEU: Loading- und Error-States
  if (loading) {
    return (
      <section className={cn(T.shell, "print:mt-0 report-section")}>
        <div className={cn(T.header, T.headerRail, tone.rail)}>
          <div className={T.headerInner}>
            <div className={T.headerMain}>
              <span className={T.eyebrow}>
                <LayoutDashboard className={cn("h-3.5 w-3.5", tone.eyebrow)} />
                {t("taxYear", language)} {yearLabel}
              </span>
              <h2 className={T.title}>
                {t("vet", language)} {t("taxTransactions", language)}
              </h2>
            </div>
            <div className={T.headerAsidePills}>
              <span className={cn(T.headerChipSoft, "text-indigo-700 border-indigo-200 bg-indigo-50/80")}>
                <span className="h-2 w-2 rounded-full bg-indigo-500" />
                <span>VET</span>
              </span>

              <HeaderIconBadge>
                <img src="/vet-icon.png" alt="VET" className="h-8 w-8 object-contain" draggable={false} />
              </HeaderIconBadge>
            </div>
          </div>
        </div>
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-slate-600">Lade VET-Daten...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={cn(T.shell, "print:mt-0 report-section")}>
        <div className={cn(T.header, T.headerRail, tone.rail)}>
          <div className={T.headerInner}>
            <div className={T.headerMain}>
              <h2 className={T.title}>
                {t("vet", language)} {t("taxTransactions", language)} – Fehler
              </h2>
            </div>
            <div className={T.headerAsidePills}>
              <span className={cn(T.headerChipSoft, "text-indigo-700 border-indigo-200 bg-indigo-50/80")}>
                <span className="h-2 w-2 rounded-full bg-indigo-500" />
                <span>VET</span>
              </span>

              <HeaderIconBadge className="print:bg-white print:shadow-none print:ring-gray-300">
                <img src="/vet-icon.png" alt="VET" className="h-8 w-8 object-contain" draggable={false} />
              </HeaderIconBadge>
            </div>
          </div>
        </div>
        <div className="p-8 text-center">
          <p className="text-red-600">{error}</p>
        </div>
      </section>
    );
  }

  const PdfTokenRowHeader = () => (
    <tr className="pdf-token-row-head">
      <th className={cn(T.th, "v3-pdf-type-col text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("type", language)}</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("txHashColumn", language)}</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("token", language)}</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("amount", language)}</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("price", language)}</span>
          <span className={cn(T.thSub, "v3-th-sub-fix")}>{currencyCode}</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("total", language)}</span>
          <span className={cn(T.thSub, "v3-th-sub-fix")}>{currencyCode}</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("taxableProfitShort", language)}</span>
          <span className={cn(T.thSub, "v3-th-sub-fix")}>({currencyCode})</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{pdfExportMode ? (language === "en" ? "Date (UTC)" : "Datum (UTC)") : t("date", language)}</span>
        </span>
      </th>
    </tr>
  );

  const renderDesktopThead = () => (
    <thead className={cn(T.thead, primaryTableHeadClass, "print:bg-white")}>
      <tr>
        <th
          className={cn(T.th, "w-[10.5%] cursor-pointer print:text-black")}
          onClick={() => handleSort("classification")}
        >
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>
              {t("type", language)}
              {getArrow("classification")}
            </span>
          </span>
        </th>

        <th
          className={cn(T.th, "w-[26.3%] cursor-pointer print:text-black")}
          onClick={() => handleSort("hash")}
        >
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>
              {t("txHashColumn", language)}
              {getArrow("hash")}
            </span>
          </span>
        </th>

        <th className={cn(T.th, "text-center w-[10.5%] print:text-black")}>
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("token", language)}</span>
          </span>
        </th>

        <th
          className={cn(T.th, "text-center w-[10.5%] cursor-pointer print:text-black")}
          onClick={() => handleSort("amount")}
        >
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>
              {t("amount", language)}
              {getArrow("amount")}
            </span>
          </span>
        </th>

        <th
          className={cn(T.th, "text-center w-[10.5%] cursor-pointer print:text-black")}
          onClick={() => handleSort("euroPrice")}
        >
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>
              {t("price", language)}
              {getArrow("euroPrice")}
            </span>
            <span className={cn(T.thSub, "v3-th-sub-fix")}>{currencyCode}</span>
          </span>
        </th>

        <th
          className={cn(T.th, "text-center w-[10.5%] cursor-pointer print:text-black")}
          onClick={() => handleSort("euroAmount")}
        >
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>
              {t("total", language)}
              {getArrow("euroAmount")}
            </span>
            <span className={cn(T.thSub, "v3-th-sub-fix")}>{currencyCode}</span>
          </span>
        </th>

        <th className={cn(T.th, "text-center w-[10.5%] print:text-black")}>
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("taxableProfitShort", language)}</span>
            <span className={cn(T.thSub, "v3-th-sub-fix")}>({currencyCode})</span>
          </span>
        </th>

        <th
          className={cn(T.th, "text-center w-[10.5%] cursor-pointer print:text-black")}
          onClick={() => handleSort("timestamp")}
        >
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>
              {pdfExportMode ? (language === "en" ? "Date (UTC)" : "Datum (UTC)") : t("date", language)}
              {getArrow("timestamp")}
            </span>
          </span>
        </th>
      </tr>
    </thead>
  );

  const renderDesktopTableFooter = () => (
    <>
      <tr className={cn("font-semibold border-t", tone.footerPrimary, "print:bg-gray-200 print:text-black")}>
        <td colSpan={6} className="px-6 py-4 text-right print:text-black">
          {getTaxCategoryLabel("disposal", selectedCountry, language)} ({t("swaps", language)}) –{" "}
          {language === "en" ? `Sum ${yearLabel}` : `Summe Steuerjahr ${yearLabel}`}:
        </td>
        <td className="px-6 py-4 font-mono print:text-black">
          {isPreviewMode ? (
            <MaskedEurCell
              value={previewMaskedTotals?.disposal || `${currencySymbol} **.**`}
              fullWidth
              currencySymbol={currencySymbol}
              currencyCode={currencyCode}
              language={language}
            />
          ) : (
            <>
              {currencySymbol} {fmtFixed(totals.swapProfit, 2)}
            </>
          )}
        </td>
        <td className="px-6 py-4" />
      </tr>
      {(totals.otherIncome !== 0 || (isPreviewMode && previewMaskedTotals?.hasOther)) && (
        <tr className={cn("font-semibold border-t", tone.footerSecondary, "print:bg-gray-200 print:text-black")}>
          <td colSpan={6} className="px-6 py-4 text-right print:text-black">
            {getTaxCategoryLabel("other", selectedCountry, language)} –{" "}
            {language === "en" ? `Sum ${yearLabel}` : `Summe Steuerjahr ${yearLabel}`}:
          </td>
          <td className="px-6 py-4 font-mono print:text-black">
            {isPreviewMode ? (
              <MaskedEurCell
                value={previewMaskedTotals?.other || `${currencySymbol} **.**`}
                fullWidth
                currencySymbol={currencySymbol}
                currencyCode={currencyCode}
                language={language}
              />
            ) : (
              <>
                {currencySymbol} {fmtFixed(totals.otherIncome, 2)}
              </>
            )}
          </td>
          <td className="px-6 py-4" />
        </tr>
      )}
    </>
  );

  const renderDesktopTxFragment = (tx, index) => {
    const maskRowEur = shouldMaskPreviewEurRow(index, filtered.length, isPreviewMode);
    const classification = (tx.classification || tx.type || "").toLowerCase();
    const isGasAbstractionFee = classification === "gas_abstraction_fee";
    const isDexExecutionCost = isDexAggregatorExecutionCostClass(classification);
    const isSwapOutLikeResult = isSwapOutLike(classification);
    const isSquirtleSquadResult = isSquirtleSquadClass(classification);
    const isV3TaxPurchase = classification === "v3tax_purchase";
    const typeLabel = (() => {
      if (isDexExecutionCost) return getDexAggregatorExecutionCostPlainLabel(tx);
      if (classification === "nfbc_purchase") {
        return language === "de" ? "NFBC Kauf" : "NFBC Purchase";
      }
      if (classification === "v3tax_purchase") {
        const plan = resolveV3TaxPlanName(tx);
        if (plan) return `V3Tax ${plan}`;
        return language === "de" ? "V3Tax Kauf" : "V3Tax purchase";
      }
      if (classification === "pause_your_carbon_purchase") {
        return "Pause Your Carbon Purchase";
      }
      if (classification === "swap_out") return "Swap_out";
      return tx.classification || tx.type || "Swap_out";
    })();
    const pdfAirdropTypeLabel =
      pdfExportMode && classification === "airdrop" ? "X2Earn Reward" : null;
    const pdfGasAbstractionTypeLabel =
      pdfExportMode && isGasAbstractionFee
        ? getGasAbstractionFeePlainLabel(language)
        : null;

    const hasFifoDetails =
      (Array.isArray(tx.fifoLegs) && tx.fifoLegs.length > 0) ||
      (Array.isArray(tx.matchedInflows) && tx.matchedInflows.length > 0);

    const isExpanded = expandedIndex === index;

    const shouldShowButton =
      hasFifoDetails ||
      isV3TaxPurchase ||
      isGasAbstractionFee ||
      (isPreviewMode && isSwapOutLikeResult);

    const showDetails = pdfExportMode
      ? hasFifoDetails && isImportantFifoTx(tx)
      : expandedIndex === index &&
        (hasFifoDetails || isPreviewMode || isV3TaxPurchase || isGasAbstractionFee);

    const previewIssueKind = getPreviewTaxIssueKind(tx);
    const showPreviewIssueRow = isPreviewMode && !!previewIssueKind;

    return (
      <React.Fragment key={index}>
        {pdfExportMode && showDetails ? <PdfTokenRowHeader /> : null}
        <tr
          className={cn(
            T.tr,
            "group pdf-tx-main-row transition-colors duration-150",
            "hover:bg-slate-50/80",
            "print:hover:bg-transparent"
          )}
        >
          <td className={cn(T.td, "min-w-0 whitespace-normal [overflow-wrap:anywhere]")}>
            {shouldShowButton ? (
              <button
                onClick={() => toggleRow(index)}
                className="block w-full min-w-0 whitespace-normal [overflow-wrap:anywhere] text-blue-600 hover:text-blue-800 font-semibold transition-colors duration-200 text-left"
                disabled={pdfExportMode}
                title={
                  isPreviewMode
                    ? language === "en"
                      ? "Preview: Fiat values locked, details visible"
                      : "Preview: Fiat-Beträge gesperrt, Details sichtbar"
                    : undefined
                }
              >
                <span className="flex w-full min-w-0 items-start gap-1">
                  {isV3TaxPurchase ? (
                    <V3TaxPurchaseTypeLabel
                      tx={tx}
                      language={language}
                      leading={isExpanded ? "▼" : "▶"}
                    />
                  ) : isGasAbstractionFee ? (
                    <span className="flex w-full min-w-0 items-start gap-1">
                      <span className="mt-0.5 flex-shrink-0" aria-hidden>
                        {isExpanded ? "\u25bc" : "\u25b6"}
                      </span>
                      <GasAbstractionFeeLabel
                        language={language}
                        token={tx.token || tx.tokenSymbol || tx.asset || "VET"}
                        className="min-w-0 whitespace-normal [overflow-wrap:anywhere]"
                      />
                    </span>
                  ) : isDexExecutionCost ? (
                    <span className="inline-flex items-start gap-1">
                      <span className="mt-0.5" aria-hidden>{isExpanded ? "▼" : "▶"}</span>
                      <DexAggregatorExecutionCostLabel tx={tx} />
                    </span>
                  ) : (
                    <>
                      <span className="mt-0.5" aria-hidden>{isExpanded ? "▼" : "▶"}</span>
                      {isSquirtleSquadResult ? (
                        renderSquirtleSquadType(classification)
                      ) : getVetDomainsDisplay(classification, tx) ? (
                        renderVetDomainsClassification(classification, tx)
                      ) : getVaultXDisplay(classification) ? (
                        <VaultXTypeLabel classification={classification} />
                      ) : (
                        <span className="min-w-0 whitespace-normal [overflow-wrap:anywhere]">{typeLabel}</span>
                      )}
                    </>
                  )}
                </span>
              </button>
            ) : isSquirtleSquadResult ? (
              renderSquirtleSquadType(classification)
            ) : getVetDomainsDisplay(classification, tx) ? (
              renderVetDomainsClassification(classification, tx)
            ) : isV3TaxPurchase ? (
              <V3TaxPurchaseTypeLabel tx={tx} language={language} />
            ) : isSwapOutLikeResult ? (
              isDexExecutionCost ? (
                <DexAggregatorExecutionCostLabel tx={tx} />
              ) : (
                <span className="block min-w-0 whitespace-normal [overflow-wrap:anywhere] text-gray-700 font-medium">
                  {pdfGasAbstractionTypeLabel || pdfAirdropTypeLabel || typeLabel}
                </span>
              )
            ) : isWovCreatorRoyaltyClass(classification) ? (
              renderWorldOfVCreatorRoyaltyType(language)
            ) : isWovAuctionBidClass(classification) ? (
              renderWorldOfVAuctionBidType(classification, language)
            ) : isJuicyFinanceClass(classification) ? (
              renderJuicyFinanceType(classification, tx.token || "VET")
            ) : isSolarwiseClass(classification) ? (
              renderSolarwiseClassification(tx.classification || "")
            ) : isGasAbstractionFee ? (
              <GasAbstractionFeeLabel
                language={language}
                token={tx.token || tx.tokenSymbol || tx.asset || "VET"}
              />
            ) : getVaultXDisplay(classification) ? (
              <VaultXTypeLabel classification={classification} />
            ) : (
              <span className="text-gray-700 font-medium">
                {pdfAirdropTypeLabel ||
                  toTitleCase(tx.classification || tx.type || "Unknown")}
              </span>
            )}
          </td>
          <td className={cn(T.td, T.mono, "break-all text-slate-700 print:text-black")}>
            {tx.hash || tx.txid || tx.event_id || "—"}
            {pdfExportMode && (tx.clause_index != null || tx.clauseIndex != null || tx.event_index != null || tx.eventIndex != null || tx.log_index != null || tx.logIndex != null) ? (
              <div className="text-[10px] text-slate-500 print:text-slate-600 mt-0.5">
                {`Event: C${tx.clause_index ?? tx.clauseIndex ?? "–"} / E${tx.event_index ?? tx.eventIndex ?? tx.log_index ?? tx.logIndex ?? "–"}`}
              </div>
            ) : null}
          </td>
          <td className={cn(T.td, "text-center text-slate-800 print:text-black")}>{tx.token || "VET"}</td>
          <td className={cn(T.td, T.mono, "text-center print:text-black")}>{Number(tx.amount || 0).toFixed(6)}</td>
          {showPreviewIssueRow ? (
            <PreviewTaxIssueCell
              language={language}
              issueKind={previewIssueKind}
              colSpan={3}
              className="print:text-black"
            />
          ) : (
            <>
              <td className={cn(T.td, T.mono, "text-center print:text-black")}>
                {maskRowEur ? (
                  <MaskedEurCell fullWidth currencySymbol={currencySymbol} currencyCode={currencyCode} language={language} />
                ) : (
                  <>{currencySymbol} {fmtFixed(pickFiatPrice(tx) ?? 0, 5)}</>
                )}
              </td>
              <td className={cn(T.td, T.mono, "text-center print:text-black")}>
                {maskRowEur ? (
                  <MaskedEurCell fullWidth currencySymbol={currencySymbol} currencyCode={currencyCode} language={language} />
                ) : (
                  <>{currencySymbol} {fmtFixed(pickFiatTotal(tx) ?? 0, 5)}</>
                )}
              </td>
              <td className={cn(T.td, "text-center print:text-black")}>
                {maskRowEur ? (
                  <MaskedEurCell fullWidth currencySymbol={currencySymbol} currencyCode={currencyCode} language={language} />
                ) : (
                  <TaxTreatmentValue
                    row={tx}
                    language={language}
                    selectedCountry={selectedCountry}
                    formattedValue={`${currencySymbol} ${fmtFixed(
                      pickFiatTaxable(tx) ?? 0,
                      5
                    )}`}
                  />
                )}
              </td>
            </>
          )}
          <td className={cn(T.td, "text-center text-slate-600 print:text-black")}>
            {formatDateFromTimestamp(tx.timestamp)}
          </td>
        </tr>
        {showDetails && (
          <FifoDetailRow
            tx={tx}
            inflows={tx.fifoLegs || tx.matchedInflows || []}
            isEditMode={isEditMode}
            nonTaxableInflows={{}}
            onNonTaxableChange={() => {}}
            selectedCountry={selectedCountry}
            isPreviewMode={isPreviewMode}
            maskRowEur={maskRowEur}
            pdfExportMode={pdfExportMode}
            language={language}
          />
        )}
        <PreviewModeSeparator
          show={shouldShowPreviewSeparatorAfterRow(index, filtered.length, isPreviewMode)}
          colSpan={8}
          language={language}
          currencyCode={currencyCode}
        />
      </React.Fragment>
    );
  };

return (
  <section
    className={cn(
      T.shell,
      "print:mt-0 report-section print:shadow-none print:border-2 print:border-gray-300",
      pdfExportMode && "rounded-none border-0 bg-white shadow-none ring-0"
    )}
  >
    <div className={cn(T.header, T.headerRail, tone.rail, "print:border-slate-200")}>
      <div className={T.headerInner}>
        <div className={T.headerMain}>
          <span className={T.eyebrow}>
            <LayoutDashboard className={cn("h-3.5 w-3.5", tone.eyebrow)} />
            {t("taxYear", language)} {yearLabel}
          </span>
          <h2 className={cn(T.title, "print:text-black")}>{t("vet", language)} {t("taxTransactions", language)}</h2>
          <p className={T.subtitle}>
            {language === "en"
              ? "Calculated VET disposal transactions and taxable income for the selected tax year"
              : "Berechnete VET-Veräußerungen und steuerpflichtige Einkünfte für das ausgewählte Steuerjahr"}
          </p>
        </div>
        {!pdfExportMode && (
          <div className={T.headerAsidePills}>
            <span className={cn(T.headerChipSoft, "text-indigo-700 border-indigo-200 bg-indigo-50/80")}>
              <span className="h-2 w-2 rounded-full bg-indigo-500" />
              <span>VET</span>
            </span>

            <HeaderIconBadge className="print:bg-white print:shadow-none print:ring-gray-300">
              <img
                src="/vet-icon.png"
                alt="VET"
                className="h-8 w-8 object-contain"
                draggable={false}
              />
            </HeaderIconBadge>
          </div>
        )}
      </div>
    </div>

    {!pdfExportMode && (
      <div className={T.toolbarWrap}>
        <div className={cn(T.toolbar, "flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between")}>
          <div className="flex-1 min-w-0">
            <input
              type="text"
              placeholder={`🔍 ${t("search", language)} ${t("type", language)}, ${t("transactionHash", language)}, ${t("amount", language)}, ${pdfExportMode ? (language === "en" ? "Date (UTC)" : "Datum (UTC)") : t("date", language)}...`}
              className={T.input}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className={T.countPill}>
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              <span className="font-semibold text-slate-800">{filtered.length}</span>
              <span>{t("transactions", language)}</span>
            </span>
            <select className={T.select} value={tokenFilter} onChange={(e) => setTokenFilter(e.target.value)}>
              <option value="ALL">🔁 {t("allTokens", language)}</option>
              <option value="VET">VET</option>
            </select>
            <select className={T.select} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="ALL">🔎 {t("allTypes", language)}</option>
              <option value="swap_out">🔄 Swap Out</option>
              <option value="squad_preico_payment">🐢 Squirtle Squad Pre-ICO Pool</option>
              {JUICY_TAX_TYPE_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
              <option value="solarwise_electricity_revenue">☀️ Solarwise Electricity Revenue</option>
              <option value="solarwise_stake_deposit">☀️ Solarwise Stake Deposit</option>
              <option value="solarwise_stake_withdrawal">☀️ Solarwise Stake Withdrawal</option>
              <option value="vet_domains_registration">🌐 vet.domains Registration</option>
              <option value="vet_domains_renewal">🌐 vet.domains Renewal</option>
              <option value="vet_domains_payment_refund">🌐 vet.domains Payment Refund</option>
            </select>
          </div>
        </div>
      </div>
    )}

    <PreviewTableNotice
      isPreviewMode={isPreviewMode}
      totalRows={filtered.length}
      language={language}
      currencyCode={currencyCode}
    />

    <div className={cn(T.tableBlock, "hidden md:block print:block")}>
      <div className={cn(T.tableWrap, primaryTableWrapClass)}>
      {filtered.length === 0 ? (
        <table className={cn(T.table, "min-w-[1160px] table-fixed print:min-w-0")}>
          {renderDesktopThead()}
          <tbody className={T.tbody}>
            <tr>
              <td colSpan={8} className="px-6 py-10 print:hidden">
                <div className="print:hidden">
                  <UnifiedTableEmptyState
                    language={language}
                    year={yearLabel}
                    scopeLabel="VET"
                    totalCount={emptyMetrics.totalCount}
                    yearCount={emptyMetrics.yearCount}
                    otherYearsCount={emptyMetrics.otherYearsCount}
                    hiddenByFilterCount={emptyMetrics.hiddenByFilterCount}
                    inTable
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        <table className={cn(T.table, "min-w-[1160px] table-fixed print:min-w-0")}>
          {renderDesktopThead()}
          <tbody className={T.tbody}>
            {filtered.map((tx, index) => renderDesktopTxFragment(tx, index))}
            {renderDesktopTableFooter()}
          </tbody>
        </table>
      )}
      </div>
    </div>

  </section>
);
}
